// Shared gift promo logic for cart drawer + cart page
window.GiftPromo = (function () {
  if (window.GiftPromo) return window.GiftPromo;
  let ext = window.GiftPromoConfig || {};
  const CONFIG = {
    THRESHOLD: ext.threshold || 300,
    GIFT_PRODUCTS: Array.isArray(ext.giftProducts) ? ext.giftProducts : [],
    GIFT_TAG: 'yes',
    START_TIME: ext.startTime || '',
    END_TIME: ext.endTime || '',
    // Waits before re-trying a failed reconciliation, then before one background re-check.
    RETRY_DELAYS: Array.isArray(ext.retryDelays) ? ext.retryDelays : [500, 1500],
    RECHECK_DELAY: typeof ext.recheckDelay === 'number' ? ext.recheckDelay : 5000,
    // A hung read keeps checkout locked; give up and take the 'error' retry path instead.
    FETCH_TIMEOUT: typeof ext.fetchTimeout === 'number' ? ext.fetchTimeout : 10000
  };

  // Resolves with parse(response), or rejects after FETCH_TIMEOUT (body included).
  function fetchWithTimeout(url, options, parse) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timer;
    const timeout = new Promise(function (resolve, reject) {
      timer = setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error('Gift promo request timed out: ' + url));
      }, CONFIG.FETCH_TIMEOUT);
    });
    const request = fetch(url, Object.assign({}, options, controller ? { signal: controller.signal } : {})).then(parse);
    return Promise.race([request, timeout]).finally(function () { clearTimeout(timer); });
  }

  function isPromoActive() {
    if (!CONFIG.GIFT_PRODUCTS.length) return false;
    if (!CONFIG.START_TIME || !CONFIG.END_TIME) return false;

    let now = new Date();
    let startTime = new Date(CONFIG.START_TIME);
    let endTime = new Date(CONFIG.END_TIME);
    if (Number.isNaN(startTime.getTime()) || Number.isNaN(endTime.getTime())) return false;
    if (endTime <= startTime) return false;
    if (now < startTime) return false;
    if (now > endTime) return false;
    return true;
  }

  const STUDIOFORGE_VARIANT_IDS = [52311667245423, 52174206108015, 52042575577455];
  // const GOT_VARIANT_IDS = [51648018547055, 51648018612591, 51648018940271];
  let isProcessing = false;

  function isGiftItem(item) {
    return item.properties && item.properties._isGift === CONFIG.GIFT_TAG;
  }

  function isStudioForgeItem(item) {
    return STUDIOFORGE_VARIANT_IDS.indexOf(item.variant_id) !== -1;
  }

  function disableCheckoutButtons() {
    document.querySelectorAll('button.cart__checkout').forEach(function (btn) {
      btn.disabled = true;
      btn.classList.add('btn--loading', 'disabled');
    });
  }

  function enableCheckoutButtons() {
    document.querySelectorAll('button.cart__checkout').forEach(function (btn) {
      btn.disabled = false;
      btn.classList.remove('btn--loading', 'disabled');
    });
  }

  function readCart(since) {
    if (window.theme && window.theme.cartReads) return window.theme.cartReads.read({ since: since });
    return fetchWithTimeout('/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    }, function (res) {
      if (!res.ok) return null;
      let contentType = res.headers.get('content-type') || '';
      if (contentType.includes('text/html')) return null;
      return res.json();
    });
  }

  function getGiftState(cart) {
    let cartTotal = cart.items.reduce(function (total, item) {
      if (isGiftItem(item) || isStudioForgeItem(item)) return total;
      // A fully discounted line has final_line_price 0, which must not fall back to line_price.
      let price = typeof item.final_line_price === 'number' ? item.final_line_price : item.line_price;
      return total + (price || 0);
    }, 0);

    let hasGift = cart.items.some(isGiftItem);
    // let hasGOT = cart.items.some(function (item) {
    //   return GOT_VARIANT_IDS.indexOf(item.variant_id) !== -1;
    // });
    let thresholdMet = (cartTotal / 100) >= CONFIG.THRESHOLD;
    let promoActive = isPromoActive();

    return {
      shouldAdd: promoActive && thresholdMet && !hasGift,
      shouldRemove: (!thresholdMet || !promoActive) && hasGift
    };
  }

  // Reconciliation outcomes: 'none' = cart already in sync, 'acted' = cart changed,
  // 'error' = state unknown or gift not added (retry, then allow checkout),
  // 'stale' = an ineligible gift is still in the cart (retry, keep checkout locked).
  async function reconcileGift(since) {
    let cart = await readCart(since);
    if (!cart) return 'error';

    if (window.ProductGiftPromo) {
      const productGiftResult = await window.ProductGiftPromo.reconcile(cart, { readCart, mutateCart });
      if (productGiftResult !== 'none') return productGiftResult;
    }

    let state = getGiftState(cart);
    if (state.shouldAdd) return await addGift();
    if (state.shouldRemove) return await removeGift(cart);
    return 'none';
  }

  function wait(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  // A check started while another is running joins it instead of reporting "no action":
  // callers re-enable checkout on false, which must wait until the running check settles.
  let running = null;
  let rerunRequested = false;
  // Refresh callbacks of every caller that joined the running check (cart page + drawer).
  let refreshCallbacks = [];
  // Set when an ineligible gift could not be removed; only a successful reconciliation
  // clears it, so a later failed cart read cannot unlock checkout.
  let staleGift = false;

  function addRefreshCallback(refreshCallback) {
    if (refreshCallback && refreshCallbacks.indexOf(refreshCallback) === -1) refreshCallbacks.push(refreshCallback);
  }

  // options.since: the cartReads mark the caller's cart HTML was requested at; a read sent
  // after it is reused. Re-runs always read fresh.
  // Resolves true while checkout must stay locked: the cart changed (the refresh re-checks it)
  // or a stale gift could not be removed. Callers re-enable checkout only on false.
  async function checkAndManageGift(refreshCallback, options) {
    if (running) {
      rerunRequested = true;
      addRefreshCallback(refreshCallback);
      return running.then(function (run) { return run.acted || run.failure === 'stale'; });
    }

    let since = options && options.since;
    refreshCallbacks = [];
    addRefreshCallback(refreshCallback);
    running = (async function () {
      let acted = false;
      let failure = null;
      let attempt = 0;
      do {
        rerunRequested = false;
        let result;
        try {
          result = await reconcileGift(since);
        } catch (e) {
          console.error('Gift promo error:', e);
          result = 'error';
        }
        since = undefined;
        if (result === 'acted') acted = true;
        if (result === 'stale') staleGift = true;
        else if (result !== 'error') staleGift = false;
        failure = staleGift ? 'stale' : result === 'error' ? 'error' : null;
        if (failure && attempt < CONFIG.RETRY_DELAYS.length) {
          await wait(CONFIG.RETRY_DELAYS[attempt++]);
          rerunRequested = true;
        }
      } while (rerunRequested);
      return { acted: acted, failure: failure };
    })();

    let run;
    let callbacks;
    try {
      run = await running;
    } finally {
      running = null;
      callbacks = refreshCallbacks;
      refreshCallbacks = [];
    }
    let refreshAll = callbacks.length ? function () {
      return Promise.all(callbacks.map(function (callback) { return callback(); }));
    } : null;

    if (run.failure) {
      console.error('Gift promo reconciliation failed:', run.failure);
      if (!(options && options.background)) scheduleRecheck(refreshAll);
    }

    // Refresh after releasing the lock: each callback re-renders its cart and runs a new check.
    if (run.acted && refreshAll) await refreshAll();
    return run.acted || run.failure === 'stale';
  }

  // One delayed re-check after retries ran out; it unlocks checkout itself because the
  // caller that locked it has already settled.
  function scheduleRecheck(refreshCallback) {
    setTimeout(function () {
      checkAndManageGift(refreshCallback, { background: true }).then(function (locked) {
        if (!locked) enableCheckout();
      });
    }, CONFIG.RECHECK_DELAY);
  }

  function mutateCart(url, options) {
    const operation = (signal) => fetch(url, signal ? Object.assign({}, options, { signal: signal }) : options);
    return window.theme && window.theme.cartReads
      ? window.theme.cartReads.mutate(operation) : operation();
  }

  async function addGift() {
    let failed = false;
    try {
      disableCheckoutButtons();

      for (let gp of CONFIG.GIFT_PRODUCTS) {
        if (!gp || !gp.handle) continue;

        let product;
        try {
          product = await fetchWithTimeout('/products/' + gp.handle + '.js', {}, function (res) {
            if (!res.ok) throw new Error('Gift product load failed: ' + res.status);
            return res.json();
          });
        } catch (e) {
          console.error('Gift promo product error:', e);
          failed = true;
          continue;
        }
        if (!product || !product.variants || !product.variants.length) continue;

        let availableVariants = product.variants.filter(function (v) { return v.available; });
        if (!availableVariants.length) continue;
        let randomVariant = availableVariants[Math.floor(Math.random() * availableVariants.length)];

        // The cart may have changed while the gift product loaded; confirm eligibility right before the POST.
        let latestCart = await readCart();
        if (!latestCart) return 'error';
        if (!getGiftState(latestCart).shouldAdd) {
          rerunRequested = true;
          return 'none';
        }

        let addRes = await mutateCart('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: randomVariant.id,
              quantity: 1,
              properties: { _isGift: CONFIG.GIFT_TAG }
            }]
          })
        });
        if (!addRes.ok) {
          failed = true;
          continue;
        }

        return 'acted';
      }

      // No gift product has an available variant: nothing to retry.
      return failed ? 'error' : 'none';
    } catch (e) {
      console.error('Gift promo add error:', e);
      return 'error';
    }
  }

  async function removeGift(cart) {
    try {
      let giftItem = cart.items.find(isGiftItem);
      if (!giftItem) return 'none';

      disableCheckoutButtons();
      const removeRes = await mutateCart('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: giftItem.key || giftItem.variant_id, quantity: 0 })
      });

      return removeRes.ok ? 'acted' : 'stale';
    } catch (e) {
      console.error('Gift promo remove error:', e);
      return 'stale';
    }
  }

  // Callers unlock after their own cart work (including failed HTML loads); an ineligible gift
  // that could not be removed keeps checkout locked until a reconciliation confirms the cart.
  // While a check runs, the unlock waits for it: a cart change re-renders and re-checks instead.
  function enableCheckout() {
    if (running) {
      running.then(function (run) {
        if (!run.acted) enableCheckout();
      }, function () { enableCheckout(); });
      return;
    }
    if (staleGift) return;
    enableCheckoutButtons();
  }

  return {
    CONFIG: CONFIG,
    disableCheckout: disableCheckoutButtons,
    enableCheckout: enableCheckout,
    checkAndManageGift: checkAndManageGift
  };
})();
// The cart page may load this asset after DOMContentLoaded; initialize after the shared queue exists.
if (window.ProductGiftPromo) window.ProductGiftPromo.initialize();
